import React from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Avatar, Paper } from '@mui/material';
import { People, History, EmojiEvents, Security } from '@mui/icons-material';

const About = () => {
  const teamMembers = [
    {
      name: 'John Smith',
      role: 'CEO & Founder',
      bio: 'With over 20 years of experience in printer technology, John founded our company with a vision to provide exceptional printer support services.',
      avatar: 'https://randomuser.me/api/portraits/men/41.jpg'
    },
    {
      name: 'Sarah Johnson',
      role: 'Technical Director',
      bio: 'Sarah leads our technical team with expertise in network printing solutions and printer security implementations.',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
    },
    {
      name: 'Michael Chen',
      role: 'Support Manager',
      bio: 'Michael ensures our support team delivers prompt and effective solutions to all our clients\' printer issues.',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Customer Success',
      bio: 'Emily focuses on building strong relationships with our clients and ensuring their complete satisfaction with our services.',
      avatar: 'https://randomuser.me/api/portraits/women/68.jpg'
    }
  ];

  const values = [
    {
      icon: <People />,
      title: 'Customer Focus',
      description: 'We prioritize our clients\' needs and work tirelessly to exceed their expectations.'
    },
    {
      icon: <History />,
      title: 'Reliability',
      description: 'Consistent, dependable service that our clients can count on time and time again.'
    },
    {
      icon: <EmojiEvents />,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our service delivery and technical expertise.'
    },
    {
      icon: <Security />,
      title: 'Integrity',
      description: 'Honest, transparent communication and ethical business practices guide everything we do.'
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          position: 'relative',
          bgcolor: 'background.paper',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 16 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom
              sx={{
                mb: 3,
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              About Us
            </Typography>
            <Typography 
              variant="h5" 
              component="h2" 
              color="text.secondary" 
              sx={{ mb: 4, lineHeight: 1.6 }}
            >
              Dedicated to Providing Excellence in Printer Support Services
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mb: 4, fontSize: '1.1rem' }}
            >
              We are a team of passionate professionals committed to delivering reliable, efficient, and secure printer support solutions for businesses of all sizes.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{ 
                mb: 3,
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Story
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
              Founded in 2010, our company began with a simple mission: to provide reliable printer support services that businesses could trust. What started as a small team of dedicated technicians has grown into a comprehensive support organization serving clients across the country.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
              Over the years, we've expanded our services to meet the evolving needs of our clients, from basic printer repairs to complex network printing solutions and security implementations. Our commitment to quality and customer satisfaction has remained constant throughout our growth.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
              Today, we're proud to be a leading provider of printer support services, with a team of certified technicians and a track record of success in helping businesses maintain efficient and secure printing environments.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box 
              sx={{ 
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                  opacity: 0.1,
                  borderRadius: 4,
                  zIndex: 0,
                }
              }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1585776245991-cf89dd7fc73a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Our Team at Work"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 4,
                  boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Our Values Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 },
          bgcolor: 'background.paper',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{ 
                mb: 2,
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Values
            </Typography>
            <Typography 
              variant="h5" 
              component="p" 
              color="text.secondary" 
              sx={{ maxWidth: 800, mx: 'auto' }}
            >
              The principles that guide our work and define who we are
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 3,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      color: 'primary.main',
                      bgcolor: 'background.default',
                      p: 2,
                      borderRadius: '50%',
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& > svg': {
                        fontSize: 40
                      }
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="600">
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            gutterBottom
            sx={{ 
              mb: 2,
              background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Our Team
          </Typography>
          <Typography 
            variant="h5" 
            component="p" 
            color="text.secondary" 
            sx={{ maxWidth: 800, mx: 'auto' }}
          >
            Meet the dedicated professionals behind our success
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {teamMembers.map((member, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 3,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                  }
                }}
              >
                <Avatar 
                  src={member.avatar} 
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    mb: 3,
                    border: '4px solid',
                    borderColor: 'primary.main'
                  }} 
                />
                <Typography variant="h5" component="h3" gutterBottom fontWeight="600">
                  {member.name}
                </Typography>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {member.role}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.bio}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default About; 